<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiAvatar, UiIcon, UiButton, generateInitials, getExpertTypeDisplay } from "@shopexperts/shared";
import type { ProjectDetail, ProjectSide } from "@/v2Projects/types";
import {
  formatShortDate,
  getStatusConfig,
  resolveLocationDisplay,
  formatLocalTime,
} from "@/v2Projects/utils/display";
import { getQuoteBudgetLabel } from "@/v2Projects/utils/quotePresentation";

const props = defineProps<{
  project: ProjectDetail;
  side: ProjectSide;
  bordered?: boolean;
}>();

const emit = defineEmits<{
  (e: "open-quote-aside"): void;
  (e: "status-action", action: string): void;
}>();

const lead = computed(() => props.project.lead);
const expert = computed(() => props.project.expert);
const client = computed(() => props.project.client);
const store = computed(() => props.project.store);
const quote = computed(() => props.project.latest_active_quote);
const status = computed(() => getStatusConfig(props.project.project_status, props.project.project_type));
const showLeadDetailsStatusBadge = computed(() =>
  !(props.project.project_status === "pending" && props.project.project_type === "booking_call"),
);

const isClientSide = computed(() => props.side === "client");
const showExpertQuoteCta = computed(() =>
  props.side === "expert" &&
  ["pending", "quoted", "in_progress"].includes(props.project.project_status),
);

const expertPrimaryAction = computed(() => {
  if (props.side !== "expert") return null;
  if (props.project.project_status === "in_progress") {
    return { label: "Submit Work", event: "submit_work" };
  }
  return null;
});

const expertSecondaryAction = computed(() => {
  if (props.side !== "expert") return null;
  if (["pending", "quoted"].includes(props.project.project_status)) {
    return { label: "Set to In Progress", event: "set_in_progress" };
  }
  return null;
});

const clientPrimaryAction = computed(() => {
  if (props.side !== "client") return null;
  if (props.project.project_status === "submitted") {
    return { label: "Complete Project", event: "complete_project" };
  }
  return null;
});

const expertName = computed(() => expert.value?.name || "Unknown Expert");
const expertInitials = computed(() => generateInitials(expertName.value));
const expertAvatar = computed(() => expert.value?.avatar ?? undefined);

const expertTypeLabel = computed(() => {
  if (!expert.value?.type && !expert.value?.role) return null;
  return getExpertTypeDisplay({
    type: expert.value?.type ?? "freelancer",
    role: expert.value?.role ?? null,
  });
});

const expertRoleLabel = computed(() => {
  const role = expert.value?.role?.trim();
  if (!role) return null;
  if (role === expertTypeLabel.value) return null;
  return role;
});

const expertLocation = computed(() => {
  const raw = expert.value?.location?.countryAndCity;
  if (!raw) return null;
  return resolveLocationDisplay(raw);
});

const expertLocalTime = computed(() => formatLocalTime(expert.value?.timezone));

const expertRating = computed(() => {
  const e = expert.value;
  if (!e || !e.overallReviewsCount || e.overallReviewsCount === 0) return null;
  return {
    average: e.overallReviewAverage.toFixed(1),
    count: e.overallReviewsCount,
  };
});

const clientName = computed(() =>
  client.value?.name || client.value?.email || "Unknown Client",
);
const clientInitials = computed(() => generateInitials(clientName.value));
const clientAvatar = computed(() => client.value?.avatar ?? undefined);

const compactQuoteLabel = computed(() => {
  if (quote.value?.status) {
    return quote.value.status.replace(/_/g, " ");
  }
  if (props.project.project_status === "pending") {
    return props.project.project_type === "booking_call" ? "Call Request" : "Quote Request";
  }
  return status.value.label;
});

const matchTypeBadge = computed(() => {
  if (props.project.project_type === "booking_call") return null;

  if (lead.value?.match_type === "client_selected") {
    return {
      label: "Direct",
      class: "bg-blue-100 text-blue-800 border-blue-200",
    };
  }

  if (lead.value?.match_type === "admin_matched") {
    return {
      label: "Matched",
      class: "bg-orange-100 text-orange-800 border-orange-200",
    };
  }

  return null;
});

const expertSideRows = computed(() => {
  const rows: Array<{
    label: string;
    value?: string;
    badge?: string;
    badgeClass?: string;
    extraBadge?: string;
    extraBadgeClass?: string;
    link?: { url: string; label: string };
  }> = [];

  rows.push({
    label: "Lead Type",
    badge: compactQuoteLabel.value,
    extraBadge: matchTypeBadge.value?.label,
    extraBadgeClass: matchTypeBadge.value?.class,
  });

  if (store.value?.title) {
    rows.push({
      label: "Store",
      link: store.value.url
        ? { url: store.value.url, label: store.value.title }
        : undefined,
      value: store.value.url ? undefined : store.value.title,
    });
  }

  if (lead.value?.budget_range) {
    rows.push({ label: "Budget", value: lead.value.budget_range });
  } else if (quote.value?.amount_cents) {
    rows.push({ label: "Budget", value: getQuoteBudgetLabel(quote.value) });
  }

  if (lead.value?.created_at) {
    rows.push({
      label: "Conversation Started",
      value: formatShortDate(lead.value.created_at),
    });
  }

  rows.push({ label: "Project ID", value: `#${props.project.id}` });

  return rows;
});

const clientSideRows = computed(() => {
  const rows: Array<{
    label: string;
    value?: string;
    badge?: string;
    badgeClass?: string;
    extraBadge?: string;
    extraBadgeClass?: string;
  }> = [];

  rows.push({
    label: "Type",
    badge: compactQuoteLabel.value,
  });

  if (lead.value?.budget_range) {
    rows.push({
      label: "Budget",
      value: lead.value.budget_range,
    });
  } else if (quote.value?.amount_cents) {
    rows.push({
      label: "Budget",
      value: getQuoteBudgetLabel(quote.value),
    });
  }

  if (lead.value?.created_at) {
    rows.push({
      label: "Request Submitted",
      value: formatShortDate(lead.value.created_at),
    });
  }

  if (expertLocation.value) {
    rows.push({
      label: "Expert Location",
      value: expertLocation.value,
    });
  }

  if (expertLocalTime.value) {
    rows.push({
      label: "Local Time",
      value: expertLocalTime.value,
    });
  }

  return rows;
});
</script>

<template>
  <div
    class="bg-white rounded-xl overflow-hidden flex flex-col max-h-full"
    :class="props.bordered ? 'border border-gray-200' : ''"
  >
    <div class="flex-1 overflow-y-auto">
      <template v-if="isClientSide && expert">
        <div class="p-5">
          <div class="flex items-start justify-between gap-3">
            <UiText as="h3" variant="p" class="font-semibold text-gray-900">
              Expert Details
            </UiText>
            <span
              :class="status.badgeClass"
              class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0"
            >
              {{ status.label }}
            </span>
          </div>

          <div class="mt-5 flex items-start gap-4">
            <div
              class="shrink-0 rounded-full bg-white p-[3px] ring-2"
              :class="status.avatarRing"
            >
              <UiAvatar
                :url="expertAvatar"
                :initials="expertInitials"
                :name="expertName"
                size="sm"
                :responsive="false"
                color-coded
              />
            </div>

            <div class="min-w-0 flex-1 pt-1">
              <UiText as="p" variant="h5" class="font-semibold text-gray-900 leading-tight">
                {{ expertName }}
              </UiText>

              <UiText
                v-if="expertTypeLabel"
                as="p"
                variant="p"
                class="mt-1 text-gray-600"
              >
                {{ expertTypeLabel }}
              </UiText>

              <UiText
                v-if="expertRoleLabel"
                as="p"
                variant="p-small"
                class="mt-0.5 text-gray-500"
              >
                {{ expertRoleLabel }}
              </UiText>

              <div v-if="expertRating" class="flex items-center gap-1 mt-1.5">
                <UiIcon name="dynamic-star" class="w-4 h-4 text-se-green" />
                <UiText as="span" variant="p-small" class="font-semibold text-gray-900">
                  {{ expertRating.average }}
                </UiText>
                <UiText as="span" variant="p-small" class="text-gray-500">
                  ({{ expertRating.count }} reviews)
                </UiText>
              </div>
            </div>
          </div>

          <div class="mt-5 divide-y divide-gray-100">
            <div
              v-for="row in clientSideRows"
              :key="row.label"
              class="flex items-center justify-between gap-4 py-2.5"
            >
              <UiText as="span" variant="p-small" class="text-gray-500">
                {{ row.label }}
              </UiText>

              <div
                v-if="row.badge || row.extraBadge"
                class="flex items-center justify-end gap-2 shrink-0 flex-wrap"
              >
                <span
                  v-if="row.badge"
                  :class="row.badgeClass ?? 'bg-blue-50 text-blue-700 border-blue-200'"
                  class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide border capitalize"
                >
                  {{ row.badge }}
                </span>

                <span
                  v-if="row.extraBadge"
                  :class="row.extraBadgeClass ?? 'bg-blue-50 text-blue-700 border-blue-200'"
                  class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide border capitalize"
                >
                  {{ row.extraBadge }}
                </span>
              </div>

              <UiText
                v-else
                as="span"
                variant="p-small"
                class="text-gray-900 font-medium text-right"
              >
                {{ row.value }}
              </UiText>
            </div>
          </div>

          <div class="mt-5 flex flex-col gap-2.5">
            <UiButton
              v-if="clientPrimaryAction"
              class="w-full"
              variant="primary"
              :label="clientPrimaryAction.label"
              @click="emit('status-action', clientPrimaryAction.event)"
            />
            <UiButton
              v-if="expert.profile_url"
              class="w-full"
              :variant="clientPrimaryAction ? 'outline' : 'primary'"
              label="Open Expert Profile"
              show-arrow
              :href="expert.profile_url"
              target="_blank"
              rel="noopener"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <div class="p-5">
          <div class="flex items-start justify-between gap-3">
            <UiText as="h3" variant="p" class="font-semibold text-gray-900">
              Lead Details
            </UiText>
            <span
              v-if="showLeadDetailsStatusBadge"
              :class="status.badgeClass"
              class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0"
            >
              {{ status.label }}
            </span>
          </div>

          <div class="mt-5 flex items-start gap-4">
            <div
              class="shrink-0 rounded-full bg-white p-[3px] ring-2"
              :class="status.avatarRing"
            >
              <UiAvatar
                :url="clientAvatar"
                :initials="clientInitials"
                :name="clientName"
                size="sm"
                :responsive="false"
                color-coded
              />
            </div>

            <div class="min-w-0 flex-1 pt-1">
              <UiText as="p" variant="h5" class="font-semibold text-gray-900 leading-tight">
                {{ clientName }}
              </UiText>

              <a
                v-if="client?.email"
                :href="`mailto:${client.email}`"
                class="hover:text-blue-700 transition-colors"
              >
                <UiText as="p" variant="p-small" class="mt-0.5 text-blue-600">
                  {{ client.email }}
                </UiText>
              </a>
            </div>
          </div>

          <div class="mt-5 divide-y divide-gray-100">
            <div
              v-for="row in expertSideRows"
              :key="row.label"
              class="flex items-center justify-between gap-4 py-2.5"
            >
              <UiText as="span" variant="p-small" class="text-gray-500">
                {{ row.label }}
              </UiText>

              <div
                v-if="row.badge || row.extraBadge"
                class="flex items-center justify-end gap-2 shrink-0 flex-wrap"
              >
                <span
                  v-if="row.badge"
                  :class="row.badgeClass ?? 'bg-blue-50 text-blue-700 border-blue-200'"
                  class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide border capitalize"
                >
                  {{ row.badge }}
                </span>

                <span
                  v-if="row.extraBadge"
                  :class="row.extraBadgeClass ?? 'bg-blue-50 text-blue-700 border-blue-200'"
                  class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide border capitalize"
                >
                  {{ row.extraBadge }}
                </span>
              </div>

              <a
                v-else-if="row.link"
                :href="row.link.url"
                target="_blank"
                rel="noopener"
                class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 transition-colors"
              >
                <UiText as="span" variant="p-small" class="font-medium truncate">
                  {{ row.link.label }}
                </UiText>
                <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M6 3h7v7M13 3L6 10" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </a>

              <UiText
                v-else
                as="span"
                variant="p-small"
                class="text-gray-900 font-medium text-right"
              >
                {{ row.value }}
              </UiText>
            </div>
          </div>

          <div class="mt-5 flex flex-col gap-2.5">
            <UiButton
              v-if="expertPrimaryAction"
              class="w-full"
              variant="primary"
              :label="expertPrimaryAction.label"
              @click="emit('status-action', expertPrimaryAction.event)"
            />
            <UiButton
              v-if="showExpertQuoteCta"
              class="w-full"
              :variant="expertPrimaryAction ? 'outline' : 'primary'"
              label="Send Quote"
              icon="file-placeholder"
              @click="emit('open-quote-aside')"
            />
            <UiButton
              v-if="expertSecondaryAction"
              class="w-full"
              variant="outline"
              :label="expertSecondaryAction.label"
              @click="emit('status-action', expertSecondaryAction.event)"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
